/**
 * Resolve an open journal row against the chain.
 *
 * Returns the machine event to apply, or `null` meaning "still genuinely
 * in-flight, ask again later". Pure with respect to storage and React — the
 * chain client is injected, so the whole decision tree is unit-testable without
 * a node.
 *
 * Resolution order is cheapest-and-most-certain first:
 *
 *   1. Receipt for a known hash. Works for EOAs and smart accounts alike, and
 *      is the only path that can distinguish success from revert.
 *   2. The flow's own on-chain verifier. One `eth_call` that answers "did this
 *      step's effect actually happen?" — e.g. the withdraw burn asks the router
 *      `getWithdrawAmount(user).depositLocked`. Far cheaper and far more
 *      reliable than hunting for a transaction we never saw the hash of, and it
 *      is the ONLY workable path for ERC-4337 smart accounts, whose EVM nonce
 *      does not move (see Collateral.jsx:165).
 *   3. Bounded nonce scan. Last resort for an EOA that signed and then lost the
 *      tab before the hash came back.
 *   4. Expiry.
 */

import { EVENTS, STATES } from "./machine";

export const DEFAULT_SCAN_BLOCKS = 30;

/**
 * How long a row may sit at `awaiting_signature` having never produced a hash.
 *
 * A wallet prompt cannot survive the page being unloaded. If the tab is closed
 * or navigated away while MetaMask is open and the user then dismisses it, NO
 * callback ever fires — so the row is left claiming a signature is in progress
 * that nothing can ever complete. `isPending` stays true, and every later visit
 * to that screen shows a button spinner for a prompt that no longer exists.
 * Until now the only thing that cleared it was the 24-hour TTL.
 *
 * Generous on purpose. A signed transaction returns its hash immediately, so
 * "no hash after fifteen minutes" means it was never signed — while a genuinely
 * broadcast transaction is found first by the verifier or the nonce scan above.
 */
export const UNSIGNED_WINDOW_MS = 15 * 60 * 1000;

function latestHash(entry) {
  return entry.hashes && entry.hashes.length ? entry.hashes[entry.hashes.length - 1] : null;
}

async function receiptForAnyKnownHash(entry, client) {
  // Newest first: after a speed-up the replacement is the one likely to be mined.
  for (let i = entry.hashes.length - 1; i >= 0; i -= 1) {
    const hash = entry.hashes[i];
    try {
      const receipt = await client.getTransactionReceipt({ hash });
      if (receipt) return receipt;
    } catch (err) {
      // viem throws TransactionReceiptNotFoundError while a tx is still in the
      // mempool. That is not an error condition — just keep looking.
    }
  }
  return null;
}

/**
 * Scan recent blocks for a transaction from this address at the recorded nonce.
 * Bounded because each block is an RPC round trip and the app runs against a
 * rate-limited public endpoint.
 */
async function findByNonceScan(entry, client, scanBlocks) {
  if (entry.nonceAtSubmit == null) return null;

  const currentNonce = await client.getTransactionCount({
    address: entry.address,
    blockTag: "latest",
  });

  // Nonce has not moved: nothing was mined from this account, so our transaction
  // is either still pending or was never broadcast. Either way, keep waiting.
  if (Number(currentNonce) <= Number(entry.nonceAtSubmit)) return null;

  const head = Number(await client.getBlockNumber());
  const floor = Math.max(0, head - scanBlocks);

  for (let n = head; n >= floor; n -= 1) {
    let block;
    try {
      // eslint-disable-next-line no-undef
      block = await client.getBlock({ blockNumber: BigInt(n), includeTransactions: true });
    } catch (err) {
      continue;
    }
    const txs = (block && block.transactions) || [];
    for (let i = 0; i < txs.length; i += 1) {
      const tx = txs[i];
      if (!tx || typeof tx !== "object") continue;
      if (
        String(tx.from || "").toLowerCase() === String(entry.address).toLowerCase() &&
        Number(tx.nonce) === Number(entry.nonceAtSubmit)
      ) {
        return tx.hash;
      }
    }
  }
  return null;
}

/**
 * @param {object}   args
 * @param {object}   args.entry    journal row
 * @param {object}   args.client   viem public client (or a stub in tests)
 * @param {object}   [args.flow]   flow descriptor; may provide `verifyOnChain`
 * @param {number}   [args.now]
 * @param {number}   [args.scanBlocks]
 * @returns {Promise<{event: string, payload: object}|null>}
 */
export async function resolveEntry({
  entry,
  client,
  flow,
  now = Date.now(),
  scanBlocks = DEFAULT_SCAN_BLOCKS,
}) {
  if (!entry || !client) return null;

  // --- 1. receipt for a hash we already know ------------------------------
  if (entry.hashes && entry.hashes.length) {
    const receipt = await receiptForAnyKnownHash(entry, client);
    if (receipt) return { event: EVENTS.RECEIPT, payload: { receipt } };
  }

  // --- 2. the flow's own on-chain verifier --------------------------------
  // Only meaningful before we have a receipt, and only while the step could
  // still be in flight.
  if (
    flow &&
    typeof flow.verifyOnChain === "function" &&
    (entry.status === STATES.AWAITING_SIGNATURE || entry.status === STATES.SUBMITTED)
  ) {
    try {
      const applied = await flow.verifyOnChain({ client, entry });
      if (applied === true) {
        // We know it happened but have no receipt to prove how. Synthesise a
        // success receipt carrying whichever hash we have (possibly none) —
        // downstream only needs "this step is done".
        return {
          event: EVENTS.RECEIPT,
          payload: {
            receipt: {
              transactionHash: latestHash(entry),
              blockNumber: null,
              status: "success",
            },
          },
        };
      }
    } catch (err) {
      // A failing verifier must never strand the row; fall through.
    }
  }

  // --- 3. bounded nonce scan (EOA only) -----------------------------------
  if (entry.accountType !== "smart" && !latestHash(entry)) {
    try {
      const hash = await findByNonceScan(entry, client, scanBlocks);
      if (hash) return { event: EVENTS.HASH_RECEIVED, payload: { hash } };
    } catch (err) {
      // RPC unavailable — try again on the next pass.
    }
  }

  // --- 4. a wallet prompt that never produced a transaction ---------------
  // Reached only after the verifier and the nonce scan have both come up empty,
  // so nothing that actually hit the chain can land here.
  if (
    entry.status === STATES.AWAITING_SIGNATURE &&
    !latestHash(entry) &&
    entry.updatedAt &&
    now - entry.updatedAt > UNSIGNED_WINDOW_MS
  ) {
    return { event: EVENTS.DROPPED, payload: {} };
  }

  // --- 5. expiry ----------------------------------------------------------
  if (entry.expiresAt && entry.expiresAt < now) {
    return { event: EVENTS.EXPIRE, payload: {} };
  }

  return null;
}
